import { formatMontant, Icon } from '@mairistem/finances-utils';
import React from 'react';
import { Table } from 'rsuite-table';
import PROJET_ROW_TYPE from '../../constants/common/projetRowType';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import type CreditFilterBar from '../../interfaces/common/CreditFilterBar';
import type Composant from '../../interfaces/common/composant';
import type ProjetRow from '../../interfaces/common/projetRow';
import TreeViewLibelleRow from '../common/treeView/TreeViewLibelleRow';
import CreditPaiementTreeViewActions from './CreditPaiementTreeViewActions';
import ModalCreditPaiement from './Modal/ModalCreditPaiement';
import ModalEngagement from './Modal/ModalEngagement';
import ModalMandate from './Modal/ModalMandate';
import './CreditPaiementTreeView.css';

export const ROW_HEIGHT = 60;
const TAB_HEIGHT_OFFSET = 280;

export const renderTreeToggle = (hasChildren: boolean, isExpanded: boolean) => (
	<Icon
		color="blue"
		{...(hasChildren && { name: `chevron ${isExpanded ? 'down' : 'right'}` })}
	/>
);

interface CreditPaiementTreeViewProps {
	data: ProjetRow[];
	metadata: any;
	filters: CreditFilterBar;
	loading: boolean;
	onLoadData(page: number, filters: CreditFilterBar): void;
	onRefresh?: () => void;
}

const CreditPaiementTreeView = ({
	data,
	metadata,
	filters,
	loading,
	onLoadData,
	onRefresh,
}: CreditPaiementTreeViewProps) => {
	const tableRef = React.useRef(null);
	const [currentRow, setCurrentRow] = React.useState<ProjetRow | null>(null);
	const [isModalEngagementOpen, setIsModalEngagementOpen] =
		React.useState(false);
	const [isModalMandateOpen, setIsModalMandateOpen] = React.useState(false);
	const [isModalCreditPaiementOpen, setIsModalCreditPaiementOpen] =
		React.useState(false);
	const { height } = useWindowDimensions();
	const tableHeight = height - TAB_HEIGHT_OFFSET;

	const renderCellValue = (value: any) => {
		if (value === undefined || value === null || value === 0) {
			return '-';
		}

		const v = parseFloat(value);
		return (
			<span className={`montant ${v < 0 ? 'negative' : ''}`}>
				{formatMontant(v)}
			</span>
		);
	};

	const handleScroll = (_scrollX, scrollY) => {
		const totalHeight = data.length * ROW_HEIGHT;
		const bottom = totalHeight - (scrollY + tableHeight);

		if (
			bottom < 0 &&
			metadata?.currentPage !== null &&
			metadata?.currentPage < metadata?.lastPage
		) {
			onLoadData(metadata.currentPage + 1, filters);
		}
	};

	const handleEngagementClick = (rowData: ProjetRow) => {
		setCurrentRow(rowData);
		setIsModalEngagementOpen(true);
	};

	const handleMandateClick = (rowData: ProjetRow) => {
		setCurrentRow(rowData);
		setIsModalMandateOpen(true);
	};

	const handleCreditPaiementClick = (rowData: ProjetRow) => {
		setCurrentRow(rowData);
		setIsModalCreditPaiementOpen(true);
	};

	const handleCloseModalEngagement = () => setIsModalEngagementOpen(false);

	const handleCloseModalMandate = () => setIsModalMandateOpen(false);

	const handleCloseModalCreditPaiement = () =>
		setIsModalCreditPaiementOpen(false);

	const getDecision = (rowData: ProjetRow): number | undefined => {
		if (rowData.type === PROJET_ROW_TYPE.PROJET) {
			return rowData.decision;
		}

		const composant = rowData as any as Composant;

		const projet = data.find(
			(projet) => projet.identifiant === composant.identifiantProjet,
		);

		return projet?.decision;
	};

	const getStatutProjet = (rowData: ProjetRow): number | undefined => {
		if (rowData.type === PROJET_ROW_TYPE.PROJET) {
			return rowData.statut;
		}

		const composant = rowData as any as Composant;

		const projet = data.find(
			(projet) => projet.identifiant === composant.identifiantProjet,
		);

		return projet?.statut;
	};

	return (
		<>
			<Table<ProjetRow, string>
				key="creditPaiementTable"
				ref={tableRef}
				data={data}
				isTree
				height={tableHeight}
				rowHeight={ROW_HEIGHT}
				onScroll={handleScroll}
				renderTreeToggle={(_expandButton, rowData, isExpanded) =>
					renderTreeToggle(rowData.children.length > 0, isExpanded)
				}
				rowKey="rowKey"
				wordWrap={false}
				loading={loading}
				virtualized
				shouldUpdateScroll={false}
				locale={{
					emptyMessage: 'Aucun crédit de paiement trouvé',
					loading: 'Chargement des crédits de paiement...',
				}}
			>
				{({ Cell, Column, HeaderCell }) => (
					<>
						<Column flexGrow={6} verticalAlign="middle" align="left">
							<HeaderCell>Libellé</HeaderCell>
							<Cell dataKey="libelle">
								{(rowData) => (
									<TreeViewLibelleRow
										code={rowData.code}
										decision={rowData.decision}
										libelle={rowData.libelle}
										isChild={rowData.type === PROJET_ROW_TYPE.COMPOSANT}
									/>
								)}
							</Cell>
						</Column>

						<Column flexGrow={4} verticalAlign="middle" align="right">
							<HeaderCell>Réalisé antérieur</HeaderCell>
							<Cell dataKey="realiseAnterieur">
								{(rowData) => renderCellValue(rowData.realiseAnterieur)}
							</Cell>
						</Column>

						<Column flexGrow={4} verticalAlign="middle" align="right">
							<HeaderCell>Crédit paiement {filters?.exercice}</HeaderCell>
							<Cell dataKey="creditPaiement">
								{(rowData) => (
									<>
										{renderCellValue(rowData.creditPaiement)}
										<Icon
											iconSet="Lucide"
											name="Eye"
											size={16}
											className="modal-opener"
											onClick={() => handleCreditPaiementClick(rowData)}
										/>
									</>
								)}
							</Cell>
						</Column>

						<Column flexGrow={4} verticalAlign="middle" align="right">
							<HeaderCell>Mandaté {filters?.exercice}</HeaderCell>
							<Cell dataKey="mandat">
								{(rowData) => (
									<>
										{renderCellValue(rowData.mandat)}
										<Icon
											iconSet="Lucide"
											name="Eye"
											size={16}
											className="modal-opener"
											onClick={() => handleMandateClick(rowData)}
										/>
									</>
								)}
							</Cell>
						</Column>

						<Column flexGrow={4} verticalAlign="middle" align="right">
							<HeaderCell>Engagé {filters?.exercice}</HeaderCell>
							<Cell dataKey="engagement">
								{(rowData) => (
									<>
										{renderCellValue(rowData.engagement)}
										<Icon
											iconSet="Lucide"
											name="Eye"
											size={16}
											className="modal-opener"
											onClick={() => handleEngagementClick(rowData)}
										/>
									</>
								)}
							</Cell>
						</Column>

						<Column flexGrow={4} verticalAlign="middle" align="right">
							<HeaderCell>Disponible {filters?.exercice}</HeaderCell>
							<Cell dataKey="disponible">
								{(rowData) => renderCellValue(rowData.disponible)}
							</Cell>
						</Column>

						<Column flexGrow={4} verticalAlign="middle" align="right">
							<HeaderCell>Crédit &gt; {filters?.exercice}</HeaderCell>
							<Cell dataKey="creditSuperieur">
								{(rowData) => renderCellValue(rowData.creditSuperieur)}
							</Cell>
						</Column>

						<Column align="center" width={200} verticalAlign="middle">
							<HeaderCell>Actions</HeaderCell>
							<Cell dataKey="actions">
								{(rowData) => (
									<CreditPaiementTreeViewActions
										decision={getDecision(rowData)}
										disponible={rowData.disponible}
										engagement={rowData.engagement}
										exercice={filters.exercice}
										hasChildren={(rowData.children?.length ?? 0) > 0}
										identifiantComposant={
											rowData.type === PROJET_ROW_TYPE.COMPOSANT
												? ((rowData as any as Composant).identifiant as number)
												: null
										}
										identifiantProjet={
											(rowData.type === PROJET_ROW_TYPE.PROJET
												? rowData.identifiant
												: (rowData as any as Composant)
														.identifiantProjet) as number
										}
										key={rowData.identifiant}
										montant={
											rowData.type === PROJET_ROW_TYPE.PROJET
												? rowData.montantAutorisation
												: (rowData as any as Composant).montantEvenement
										}
										onRefresh={onRefresh}
										type={rowData.type}
										statutProjet={getStatutProjet(rowData)}
									/>
								)}
							</Cell>
						</Column>
					</>
				)}
			</Table>

			{isModalEngagementOpen && (
				<ModalEngagement
					onClose={handleCloseModalEngagement}
					projetRow={currentRow}
					exercice={filters?.exercice}
				/>
			)}

			{isModalMandateOpen && (
				<ModalMandate
					onClose={handleCloseModalMandate}
					projetRow={currentRow}
					exercice={filters?.exercice}
				/>
			)}

			{isModalCreditPaiementOpen && (
				<ModalCreditPaiement
					onClose={handleCloseModalCreditPaiement}
					projetRow={currentRow}
					exercice={filters?.exercice}
				/>
			)}
		</>
	);
};

export default CreditPaiementTreeView;
